import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetState } from '../../AppState';
import { platformService } from '../../shared/services/platform.service';
import { FILTERS_STRINGS } from '../../shared/utils/constants';
import { TemplateListDialog } from '../../templates/TemplateListDialog';
import { defaultCustomFormStateValue } from '../../templates/CustomForms/CustomForm.Interface';
import { TicketCustomForm } from '../../shared/interfaces/ticket.interface';

const api = new platformService();

/**
 * The ticket's template with all its settings. TICKET_DETAILS selects only its name, so the dialog read the summary card
 * as off and saved at the end of the forms without showing it; the ticket page swaps in the full template from its
 * templates list (EditTicketForm, SET_TEMPLATE). TicketTemplate is scoped: a template the agent may not use comes back
 * empty, and the ticket's own Template stays.
 */
const fullTemplate = async (templateId: number | null | undefined) => {
  if (!templateId) return undefined;
  try {
    const { data } = await api.getTicketTemplates(`?$filter=Id eq ${templateId}&$expand=TemplateForms($expand=Form)`);
    return data?.value?.[0];
  } catch {
    return undefined;
  }
};

// The parts of the ticket page's form reducer (EditTicketForm) the form dialog dispatches to.
const formReducer = (state, action) => {
  switch (action.type) {
    case 'set-handle-customform':
      return { ...state, ...action.data };
    case 'update-ticket':
      return { ...state, UpdatedTicket: { ...state.UpdatedTicket, ...action.data } };
    case 'TemplateHasError':
    case 'TemplateErrorMessage':
    case 'IsTemplateSelectionSaving':
      return { ...state, [action.type]: action.data };
    case 'load':
      return action.data;
    default:
      return state;
  }
};

/** A form's answers as the dialog saves them - the ticket's own CustomForms row. */
export type SavedForm = TicketCustomForm;

interface FormAnswersDialogProps {
  ticketId: number;
  onClose: () => void;
  /** An answer-now save starting, with the answers (none for a Send): the list puts them in the row at once and marks the cells. */
  onSaving: (customForms: SavedForm[]) => void;
  onSaved: () => void;
  onFailed: (error: unknown) => void;
}

/**
 * A ticket's forms from a list cell: the ticket page's own dialog (TemplateListDialog in its "updateAnswer" mode) - send
 * the form, or answer it now and walk the template's forms with their rules - hosted with the ticket loaded as the
 * ticket page loads it. The shared custom form state is cleared on the way out, so nothing of this ticket's forms is left
 * for the next dialog to start from. An answer-now save is shown on the list's cells, not with a toast.
 */
export const FormAnswersDialog = ({ ticketId, onClose, onSaving, onSaved, onFailed }: FormAnswersDialogProps) => {
  const { t } = useTranslation();
  const setAppState = useSetState();
  const [formState, dispatch] = useReducer(formReducer, null);
  const [dataSourceList, setDataSourceList] = useState([]);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.getTicket(ticketId, FILTERS_STRINGS.TICKET_DETAILS),
      api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`),
    ]).then(async ([ticket, dataSources]) => {
      const template = await fullTemplate(ticket.data?.TemplateId);
      if (!live) return;
      setDataSourceList(dataSources.data.value ?? []);
      // As Update Answers on the ticket page: both send options and answer now, with answer now chosen.
      setAppState(prev => ({ ...prev, openSendFormDialogFrom: 'updateAnswer', answerCustomFormNow: true }));
      dispatch({
        type: 'load',
        data: {
          ...ticket.data,
          ...(template ? { Template: template } : {}),
          SelectedTemplate: {},
          UpdatedTicket: {},
          TemplateHasError: false,
          TemplateErrorMessage: '',
          IsTemplateSelectionSaving: false,
        },
      });
    }).catch(() => { if (live) onClose(); });
    return () => { live = false; };
  }, [ticketId]);

  const close = () => {
    setAppState(prev => ({
      ...prev,
      answerCustomFormNow: false,
      openSendFormDialogFrom: 'applyTemplate',
      customFormState: { ...defaultCustomFormStateValue },
    }));
    onClose();
  };

  // The ticket page turns this off when it reloads the ticket; here the list reloads instead.
  const savingDone = () => setAppState(prev => ({ ...prev, customFormState: { ...prev.customFormState, isCustomFormSaving: false } }));

  if (!formState) return null;

  // Named as the resolution note dialog opened from a cell names its ticket (InlineStatusCell); the dialog cuts it to
  // its width rather than to a character count.
  const header = `${t('ticket-details.edit-ticket-form.custom-form-answer.title')} – #${ticketId} ${formState.Title ?? ''}`.trim();

  return (
    <TemplateListDialog
      isNewTicket={false}
      formState={formState}
      showTemplate={true}
      isApplyingTemplateWithLifecycle={false}
      templateList={[]}
      templateListLoaded={true}
      dataSourceList={dataSourceList}
      dispatch={dispatch}
      closeApplyTemplate={close}
      applyTemplate={() => undefined}
      ticketApprovals={[]}
      header={header}
      onAnswersSaving={onSaving}
      onAnswersSaved={() => { savingDone(); onSaved(); }}
      onAnswersFailed={error => { savingDone(); onFailed(error); }}
      onFormSending={() => onSaving([])}
      onFormSent={sent => (sent ? onSaved() : onFailed(new Error('The form was not sent')))}
    />
  );
};
